"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePaymentCompletionCleanup } from "@/features/checkout/hooks/use-payment-completion-cleanup";
import type { Order } from "@/features/checkout/types";
import {
  getPendingPayment,
  isTerminalPaymentStatus,
  setFailedPaymentReturn,
} from "@/features/checkout/utils";
import { GET } from "@/lib/methods";

export const PaymentReturnReconciler = () => {
  const handlePaymentCompletion = usePaymentCompletionCleanup();
  const isCheckingRef = useRef(false);

  const reconcilePendingPayment = useCallback(async () => {
    const pendingPayment = getPendingPayment();

    if (!pendingPayment || isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const order = await GET<Order>({
        url: `/api/order?id=${pendingPayment.orderId}`,
      });

      const payment = order.payments.find(
        (item) => item.id === pendingPayment.paymentId,
      );

      if (!(payment && isTerminalPaymentStatus(payment.status))) {
        return;
      }

      if (payment.status === "CANCELED" || payment.status === "REJECTED") {
        setFailedPaymentReturn({
          cartType: order.type || pendingPayment.cartType,
          orderId: pendingPayment.orderId,
          paymentStatus: payment.status,
        });
      }

      handlePaymentCompletion({
        orderType: order.type || pendingPayment.cartType,
        paymentStatus: payment.status,
      });
    } catch {
      return;
    } finally {
      isCheckingRef.current = false;
    }
  }, [handlePaymentCompletion]);

  useEffect(() => {
    void reconcilePendingPayment();
  }, [reconcilePendingPayment]);

  return null;
};
